import React, { useState, useEffect, useCallback } from 'react';
import { teamReportApi, dailyLogApi, projectApi, teamApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, CheckCircle, Warning, Lock, ClipboardText } from '@phosphor-icons/react';

export default function TeamReportsPage() {
  const [reports, setReports] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterProject, setFilterProject] = useState('');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredTeams, setFilteredTeams] = useState([]);

  // Submission check per team
  const [teamStatuses, setTeamStatuses] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    project_id: '', team_id: '', report_date: '', summary: '', issues: '', next_day_plan: ''
  });
  const [formTeams, setFormTeams] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), teamApi.list()])
      .then(([pRes, tRes]) => { setProjects(pRes.data); setTeams(tRes.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadData = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterDate) params.report_date = filterDate;
    const res = await teamReportApi.list(params);
    setReports(res.data);

    // Also load daily logs to show summary
    const logParams = {};
    if (filterProject) logParams.project_id = filterProject;
    if (filterDate) logParams.log_date = filterDate;
    const logsRes = await dailyLogApi.list(logParams);
    setDailyLogs(logsRes.data);
  }, [filterProject, filterDate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (filterProject) {
      teamApi.list(filterProject).then(r => setFilteredTeams(r.data));
    } else {
      setFilteredTeams(teams);
    }
  }, [filterProject, teams]);

  // Check submission status for each team
  useEffect(() => {
    if (!filterDate || filteredTeams.length === 0) return;
    const checkAll = async () => {
      const statuses = {};
      for (const team of filteredTeams) {
        try {
          const res = await teamReportApi.checkSubmission(team.id, filterDate);
          statuses[team.id] = res.data;
        } catch {
          statuses[team.id] = { submitted: false, has_daily_logs: false, can_checkout: false };
        }
      }
      setTeamStatuses(statuses);
    };
    checkAll();
  }, [filteredTeams, filterDate, reports]);

  useEffect(() => {
    if (form.project_id) teamApi.list(form.project_id).then(r => setFormTeams(r.data));
    else setFormTeams([]);
  }, [form.project_id]);

  const openSubmitModal = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    setForm({
      project_id: team?.project_id || filterProject || '',
      team_id: teamId,
      report_date: filterDate,
      summary: '', issues: '', next_day_plan: ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await teamReportApi.create(form);
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit report');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report and unlock daily logs?')) return;
    try { await teamReportApi.delete(id); loadData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Group daily logs by team
  const logsByTeam = {};
  dailyLogs.forEach(l => {
    if (!logsByTeam[l.team_id]) logsByTeam[l.team_id] = [];
    logsByTeam[l.team_id].push(l);
  });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="team-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Team Reports</h1>
          <p className="text-sm text-[#666666]">Daily team submission and checkout validation</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="filter-date" />
      </div>

      {/* Team Status Cards */}
      <div className="space-y-2" data-testid="team-status-list">
        {filteredTeams.length === 0 ? (
          <Card className="p-6 text-center text-[#666666]">No teams found</Card>
        ) : filteredTeams.map(team => {
          const status = teamStatuses[team.id] || {};
          const teamLogs = logsByTeam[team.id] || [];
          const report = reports.find(r => r.team_id === team.id);

          return (
            <Card key={team.id} className={`p-3 sm:p-4 ${status.submitted ? 'border-l-4 border-l-[#00C853]' : status.has_daily_logs ? 'border-l-4 border-l-[#FFAB00]' : ''}`} data-testid={`team-status-${team.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {status.submitted ? (
                      <CheckCircle className="w-5 h-5 text-[#00C853]" weight="fill" />
                    ) : status.has_daily_logs ? (
                      <Warning className="w-5 h-5 text-[#FFAB00]" weight="fill" />
                    ) : (
                      <ClipboardText className="w-5 h-5 text-[#E0E0E0]" />
                    )}
                    <span className="font-semibold text-sm text-[#0A0A0A]">{team.name}</span>
                    <Badge variant="primary">{team.category}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#666666] ml-7">
                    <span>{teamLogs.length} daily log{teamLogs.length !== 1 ? 's' : ''}</span>
                    {status.submitted && <span className="flex items-center gap-0.5 text-[#00C853]"><Lock className="w-3 h-3" />Submitted & Locked</span>}
                    {!status.submitted && status.has_daily_logs && <span className="text-[#FFAB00]">Pending submission</span>}
                    {!status.submitted && !status.has_daily_logs && <span className="text-[#999]">No logs — cannot submit</span>}
                  </div>

                  {/* Show report details if submitted */}
                  {report && (
                    <div className="mt-2 ml-7 text-xs text-[#666]">
                      <p className="font-medium text-[#333]">{report.summary}</p>
                      {report.issues && <p className="mt-0.5 text-[#FF3333]">Issues: {report.issues}</p>}
                      {report.next_day_plan && <p className="mt-0.5">Next: {report.next_day_plan}</p>}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {!status.submitted && status.has_daily_logs && (
                    <Button size="sm" onClick={() => openSubmitModal(team.id)} data-testid={`submit-report-btn-${team.id}`}>
                      Submit Report
                    </Button>
                  )}
                  {report && (
                    <button onClick={() => handleDelete(report.id)} className="p-1 text-[#666] hover:text-[#FF3333]" title="Delete report & unlock" data-testid={`delete-report-${team.id}`}>
                      <Trash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Submit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Submit Team Report">
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="team-report-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm" data-testid="report-form-error">{error}</div>}

          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required data-testid="report-project-select">
            <option value="">-- Select Project --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>

          <div className="bg-[#F9F9F9] rounded-lg p-2 text-sm">
            <span className="text-[#666]">Team: </span>
            <span className="font-medium">{teams.find(t => t.id === form.team_id)?.name || '-'}</span>
            <span className="text-[#666] ml-3">Date: </span>
            <span className="font-medium">{form.report_date}</span>
          </div>

          <Input label="Work Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required placeholder="Summarize today's work" data-testid="report-summary-input" />
          <Input label="Issues / Blockers" value={form.issues} onChange={(e) => setForm({ ...form, issues: e.target.value })} placeholder="Any issues faced" data-testid="report-issues-input" />
          <Input label="Next Day Plan" value={form.next_day_plan} onChange={(e) => setForm({ ...form, next_day_plan: e.target.value })} placeholder="Plan for tomorrow" data-testid="report-plan-input" />

          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-report-btn">{saving ? 'Submitting...' : 'Submit & Lock'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
